import usePagination from "@/hooks/usePagination";
import { ChangeRequestTable } from "@/features/global/components/ChangeRequestTable";
import { ApolloError, useQuery } from "@apollo/client";
import { type Path, useNavigate, useParams } from "@/router";
import { GET_PERSON_CHANGE_REQUESTS_QUERY, Node } from "./queries";
import { ChangeRequestStatus } from "@/pages/sections/[sectionId]/change-requests/queries";
import { useEffect, useState } from "react";
import { useParams as useUrlParams } from "@/hooks/useSearchParams";
import { TableControls, TableFooter, buildActionColumn } from "@/components/Table";
import Select from "@/components/Select";
import { buildSectionChangeRequestListPath, formatStage } from "@/pages/sections/[sectionId]/change-requests/_utils";
import { sendToRaygun } from "@/lib/raygunUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { NoteNotableValues } from "@/types";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { usePersonChangeHistoryAccess } from "./_usePersonChangeHistoryAccess";

const path = "/people/:pgaId/person/change-requests";

const emptyAction = (pgaId: string) => ({
  children: "View Change History",
  to: "/people/:pgaId/person/change-history" as const,
  params: { pgaId }
});

export default function ChangeRequests() {
  const { loaded, canViewPersonChangeHistory } = usePersonChangeHistoryAccess();
  const { pgaId } = useParams(path);
  const navigate = useNavigate();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { params: urlParams, addSearchParam, deleteSearchParams } = useUrlParams();
  const stage = (urlParams.stage as ChangeRequestStatus) || undefined;
  const [noteChangeRequestId, setNoteChangeRequestId] = useState<string | null>(null);

  function setStage(value: ChangeRequestStatus | undefined) {
    if (value) {
      addSearchParam("stage", value);
    } else {
      deleteSearchParams("stage");
    }
  }
  const { setError } = useErrorBanner();
  const { Menu, setMenu } = useTableRowMenu();
  const canViewSection = useHasCapabilityBool({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewPeople });

  function onError(error: ApolloError) {
    sendToRaygun(error);
    setError(error.message);
  }

  const { data, loading } = useQuery(GET_PERSON_CHANGE_REQUESTS_QUERY, {
    onError,
    variables: { pgaId, stage, ...paginationVariables },
    errorPolicy: "all",
    skip: !loaded || !canViewPersonChangeHistory
  });

  useEffect(() => {
    resetPagination();
  }, [stage, resetPagination]);

  const actionColumn = buildActionColumn<Node>({
    getActions: (changeRequest) => [
      {
        text: "Add Note",
        onClick: () => setNoteChangeRequestId(changeRequest.id)
      },
      ...(canViewSection
        ? [
            {
              text: "View On Section",
              onClick: () =>
                navigate(
                  buildSectionChangeRequestListPath(changeRequest.section.id, {
                    stage: changeRequest.status,
                    expandChanges: changeRequest.id
                  }) as Path
                )
            } as const
          ]
        : [])
    ],
    actionId,
    setMenu
  });

  const emptyCaption = "This person does not have any past or current change requests.";
  const changeRequests = (data?.person.changeRequests.nodes || []).filter(Boolean) as Node[];

  const {
    expandRowId: deepLinkExpandNotesRowId,
    highlightNoteId: deepLinkHighlightNoteId,
    tableRef
  } = useNoteDeepLink({
    dataReady: !loading && changeRequests.length > 0
  });

  const { highlightRowId } = useRowHighlight({
    dataReady: !loading && changeRequests.length > 0,
    containerRef: tableRef
  });

  if (!loaded || !canViewPersonChangeHistory) {
    return null;
  }

  return (
    <div ref={tableRef}>
      <AddEditNoteSideSheet
        open={!!noteChangeRequestId}
        onClose={() => setNoteChangeRequestId(null)}
        notableId={noteChangeRequestId || ""}
        notableType={NoteNotableValues.MEMBERSHIP_CHANGE_REQUEST}
        refetchQueries={[GET_PERSON_CHANGE_REQUESTS_QUERY]}
        onSaveSuccess={() => setNoteChangeRequestId(null)}
        pgaId={pgaId}
        objectType="person"
      />
      <Menu />
      <div className="pt-12" />
      <ChangeRequestTable
        data-testid="change-requests-table"
        loading={loading}
        data={changeRequests}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.person.changeRequests)} />}
        renderControls={() => (
          <TableControls pagination={createHeaderPagination(data?.person.changeRequests)}>
            <div className="w-full sm:min-w-[200px] sm:max-w-[200px]">
              <Select
                value={stage || "ALL"}
                label="Stage"
                dropdownHeight="full"
                onSelect={(value) => setStage(value === "ALL" ? undefined : (value as ChangeRequestStatus))}
                options={[
                  { value: "ALL", displayText: "Show All", border: true },
                  ...Object.keys(ChangeRequestStatus).map((s) => ({
                    value: s,
                    displayText: formatStage(s as ChangeRequestStatus)
                  }))
                ]}
              />
            </div>
          </TableControls>
        )}
        columns={["submittedAt", "type", "change", "notes", "status", actionColumn]}
        hasActiveFilters={!!stage}
        emptyAction={emptyAction(pgaId)}
        emptyCaption={emptyCaption}
        noteEditing={{
          enabled: true,
          notableType: NoteNotableValues.MEMBERSHIP_CHANGE_REQUEST,
          refetchQueries: [GET_PERSON_CHANGE_REQUESTS_QUERY],
          pgaId,
          objectType: "person"
        }}
        deepLinkExpandNotesRowId={deepLinkExpandNotesRowId}
        deepLinkHighlightNoteId={deepLinkHighlightNoteId}
        highlightRowId={highlightRowId}
      />
    </div>
  );
}
